import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Globe,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  UserRound,
} from 'lucide-react-native';
import { memo, useCallback, useState } from 'react';
import { Linking, Platform, Pressable, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { BusinessChannels, ResearchContact } from '../types';
import { redactUserFacingText } from '../utils/safety';

const CHANNEL_ICON_SIZE = 12;
const CHANNEL_ICON_STROKE = 1.8;

interface CompanyContactListProps {
  readonly contacts: ResearchContact[];
  readonly companyName: string;
}

export function CompanyContactList({ contacts, companyName }: CompanyContactListProps) {
  const [expanded, setExpanded] = useState(false);

  if (contacts.length === 0) return null;

  const label = contacts.length === 1 ? '1 contact' : `${contacts.length} contacts`;
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <Box marginTop="xs" borderTopWidth={1} borderTopColor={colors.border.subtle} paddingTop="xs">
      <Pressable
        onPress={() => setExpanded((current) => !current)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${expanded ? 'Hide' : 'Show'} ${label} at ${companyName}`}
        style={styles.toggle}
        hitSlop={8}
      >
        <UserRound size={12} color={colors.text.secondary} strokeWidth={1.8} />
        <Text variant="caption" color={colors.text.secondary} style={styles.toggleText}>
          {label}
        </Text>
        <Chevron size={12} color={colors.text.tertiary} strokeWidth={1.8} />
      </Pressable>

      {expanded && (
        <Box marginTop="xs" gap="xs">
          {contacts.map((contact, index) => (
            <ContactRow
              key={`${contact.profileUrl ?? contact.fullName}-${index}`}
              contact={contact}
              onOpenUrl={openExternalUrl}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function openExternalUrl(url: string): void {
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(url);
  }
}

interface ContactRowProps {
  readonly contact: ResearchContact;
  readonly onOpenUrl: (url: string) => void;
}

function ContactRow({ contact, onOpenUrl }: ContactRowProps) {
  if (contact.source === 'business_listing') {
    return <BusinessChannelRow contact={contact} onOpenUrl={onOpenUrl} />;
  }

  const name = redactUserFacingText(contact.fullName);
  // The employee scraper returns a job title; harvested engagers only carry a
  // headline, so fall back rather than leaving the row without a role.
  const role = contact.jobTitle ?? contact.headline;

  return (
    <Box flexDirection="row" alignItems="center" gap="sm" style={styles.row}>
      <Box flex={1}>
        <Box flexDirection="row" alignItems="center" gap="xxs">
          <Text variant="caption" color={colors.text.primary} style={styles.name} numberOfLines={1}>
            {name}
          </Text>
          {contact.verified ? (
            <BadgeCheck
              size={11}
              color={colors.agentSource.linkedin.solid}
              strokeWidth={2}
              accessibilityLabel="Verified profile"
            />
          ) : null}
        </Box>
        {role ? (
          <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
            {redactUserFacingText(role)}
          </Text>
        ) : null}
      </Box>

      {contact.profileUrl ? (
        <Pressable
          onPress={() => onOpenUrl(contact.profileUrl as string)}
          accessibilityRole="link"
          accessibilityLabel={`View ${name} on LinkedIn`}
          style={styles.profileLink}
          hitSlop={8}
        >
          <Linkedin size={12} color={colors.agentSource.linkedin.solid} strokeWidth={1.8} />
        </Pressable>
      ) : null}
    </Box>
  );
}

type BusinessChannel = keyof BusinessChannels;

function BusinessChannelRow({ contact, onOpenUrl }: ContactRowProps): React.JSX.Element {
  const name = redactUserFacingText(contact.fullName);
  const channels: BusinessChannels = contact.channels ?? {};

  return (
    <Box gap="xxs" style={styles.row}>
      <Text variant="caption" color={colors.text.primary} style={styles.name} numberOfLines={1}>
        {name}
      </Text>
      <Box flexDirection="row" flexWrap="wrap" gap="sm">
        {channels.phone ? (
          <ChannelLink
            channel="phone"
            url={`tel:${channels.phone.replace(/[^\d+]/g, '')}`}
            label={channels.phone}
            accessibilityLabel={`Call ${name}`}
            onOpenUrl={onOpenUrl}
          />
        ) : null}
        {channels.email ? (
          <ChannelLink
            channel="email"
            url={`mailto:${channels.email}`}
            label={channels.email}
            accessibilityLabel={`Email ${name}`}
            onOpenUrl={onOpenUrl}
          />
        ) : null}
        {channels.website ? (
          <ChannelLink
            channel="website"
            url={channels.website}
            label="Website"
            accessibilityLabel={`Visit ${name} website`}
            onOpenUrl={onOpenUrl}
          />
        ) : null}
        {channels.instagram ? (
          <ChannelLink
            channel="instagram"
            url={channels.instagram}
            label="Instagram"
            accessibilityLabel={`View ${name} on Instagram`}
            onOpenUrl={onOpenUrl}
          />
        ) : null}
      </Box>
    </Box>
  );
}

interface ChannelLinkProps {
  readonly channel: BusinessChannel;
  readonly url: string;
  readonly label: string;
  readonly accessibilityLabel: string;
  readonly onOpenUrl: (url: string) => void;
}

const CHANNEL_ICONS: Record<BusinessChannel, typeof Phone> = {
  phone: Phone,
  email: Mail,
  website: Globe,
  instagram: Instagram,
};

const ChannelLink = memo(function ChannelLink({
  channel,
  url,
  label,
  accessibilityLabel,
  onOpenUrl,
}: ChannelLinkProps): React.JSX.Element {
  const mixpanel = useMixpanel();
  const Icon = CHANNEL_ICONS[channel];
  const iconColor =
    channel === 'instagram' ? colors.agentSource.instagram.solid : colors.text.interactive;

  const handlePress = useCallback(() => {
    trackEvent(mixpanel, 'prospect_search.contact_channel_opened', { channel });
    onOpenUrl(url);
  }, [mixpanel, channel, onOpenUrl, url]);

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      style={styles.channelLink}
      hitSlop={tokens.spacing.sm}
    >
      <Icon size={CHANNEL_ICON_SIZE} color={iconColor} strokeWidth={CHANNEL_ICON_STROKE} />
      <Text variant="caption" color={colors.text.interactive} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  toggleText: {
    fontWeight: '600',
  },
  row: {
    paddingVertical: 2,
  },
  name: {
    fontWeight: '600',
    flexShrink: 1,
  },
  channelLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xxs,
  },
  profileLink: {
    padding: 4,
    borderRadius: tokens.radius.sm,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
});
